import "./ProductsPage.scss";

import React, { useEffect, useState } from "react";
import {
  faClockRotateLeft,
  faMoneyBillTransfer,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";

import AppLoading from "../../commons/AppLoading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../commons/HallAppToast";
import MenuTitle from "../../commons/menu/MenuTitle";
import ModalPayProductCommission from "./components/ModalPayProductCommission";
import ProductCommissionAPI from "./ProductCommissionAPI";
import { useNavigate } from "react-router-dom";

const productCommissionAPI = new ProductCommissionAPI();

function toMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export default function ProductCommissionsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [commissionSummary, setCommissionSummary] = useState([]);
  const [isPaying, setIsPaying] = useState(false);
  const [payingEmployeeId, setPayingEmployeeId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);

  const navigate = useNavigate();

  const loadCommissionSummary = async () => {
    try {
      setIsLoading(true);

      const response = await productCommissionAPI.findSummary();
      setCommissionSummary(response?.data || response || []);
    } catch (e) {
      setCommissionSummary([]);
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCommissionSummary();
  }, []);

  const handleOpenPayModal = (item) => {
    setSelectedEmployee(item);
    setShowPayModal(true);
  };

  const handleClosePayModal = () => {
    if (isPaying) return;

    setShowPayModal(false);
    setSelectedEmployee(null);
  };

  const handleConfirmPayCommission = async ({
    employeeId,
    paymentMethod,
    observation,
  }) => {
    try {
      setIsPaying(true);
      setPayingEmployeeId(employeeId);

      await productCommissionAPI.payCommission({
        employeeId,
        paymentMethod,
        observation,
      });

      HallAppToast.success?.("Pagamento realizado com sucesso.");
      handleClosePayModal();
      await loadCommissionSummary();
    } catch (e) {
      HallAppToast.genericError();
      throw e;
    } finally {
      setIsPaying(false);
      setPayingEmployeeId(null);
    }
  };

  const handleOpenDetails = (item) => {
    navigate(`/produtos/comissoes/${item.employeeId}`);
  };

  return (
    <div className="container products-page">
      <MenuTitle
        title="Comissões de produtos"
        subtitle="Acompanhe os valores por profissional, pagamentos e histórico."
        showArrowInfo={false}
      />

      <div className="products-page__tabs">
        <button
          type="button"
          className="products-page__tab-button"
          onClick={() => navigate("/estoque")}
        >
          Produtos
        </button>

        <button type="button" className="products-page__tab-button active">
          Comissões
        </button>
      </div>

      <section className="products-page__commissions">
        <div className="products-page__commissions-header">
          <div>
            <h3>Profissionais</h3>
            <p>
              Todos os profissionais aparecem aqui, mesmo sem comissão pendente.
            </p>
          </div>
        </div>

        {isLoading ? (
          <AppLoading />
        ) : commissionSummary.length === 0 ? (
          <div className="products-page__commissions-empty">
            Nenhum profissional encontrado.
          </div>
        ) : (
          <div className="products-page__commissions-grid">
            {commissionSummary.map((item) => {
              const isCurrentPaying = payingEmployeeId === item.employeeId;

              return (
                <div
                  className="products-page__commission-card"
                  key={item.employeeId}
                >
                  <div className="products-page__commission-card-top">
                    <div className="products-page__commission-avatar">
                      <FontAwesomeIcon icon={faUserTie} />
                    </div>

                    <div className="products-page__commission-main">
                      <h4>{item.employeeName}</h4>
                      <span>
                        Pendente: <strong>{toMoney(item.totalPending)}</strong>
                      </span>
                    </div>
                  </div>

                  <div className="products-page__commission-info">
                    <div className="products-page__commission-row">
                      <span>Total comissão</span>
                      <strong>{toMoney(item.totalCommission)}</strong>
                    </div>

                    <div className="products-page__commission-row">
                      <span>Total pago</span>
                      <strong>{toMoney(item.totalPaid)}</strong>
                    </div>

                    <div className="products-page__commission-row pending">
                      <span>Total pendente</span>
                      <strong>{toMoney(item.totalPending)}</strong>
                    </div>
                  </div>

                  <div className="products-page__commission-actions">
                    <button
                      type="button"
                      className="products-page__commission-secondary-btn"
                      onClick={() => handleOpenDetails(item)}
                      disabled={isPaying}
                    >
                      <FontAwesomeIcon icon={faClockRotateLeft} />
                      <span>Ver histórico</span>
                    </button>

                    <button
                      type="button"
                      className="products-page__commission-pay-btn"
                      onClick={() => handleOpenPayModal(item)}
                      disabled={Number(item.totalPending || 0) <= 0 || isPaying}
                    >
                      <FontAwesomeIcon icon={faMoneyBillTransfer} />
                      <span>
                        {isCurrentPaying ? "Processando..." : "Pagar agora"}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ModalPayProductCommission
        open={showPayModal}
        employee={selectedEmployee}
        isLoading={isPaying}
        onClose={handleClosePayModal}
        onConfirm={handleConfirmPayCommission}
      />
    </div>
  );
}